import React, { useEffect, useMemo, useState } from "react"
import { Badge, Button, Card, Form, InputGroup, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import moment from "moment"
import Settings from "@/common/Settings"
import Pagination from "@/components/core/Pagination"
import { Routes } from "@/routes"

interface PlaceRecord {
  id: string
  name: string
  address?: string
  sector?: string
  city?: string
  image?: string
  image2?: string
  created?: string | Date
  updated?: string | Date
  createdDate?: string | Date
  updatedDate?: string | Date
  assignedToObj?: {
    firstName: string
    lastName: string
  }
  assignedTo?: string
  stageId?: string
  placeStatusId?: string
  isInPriority?: boolean
  priorityAssignedTo?: string
  priorityTo?: unknown
  priorityAssignmentDate?: string | Date
  revisitAssignedTo?: string
  revisitTo?: unknown
  revisitAssignmentDate?: string | Date
  revisitPending?: boolean
  stage?: { name: string }
  placeStatus?: { name: string; color?: string; icon?: string }
  category?: { name: string }
}

interface SelectOption {
  id: string
  name?: string
  firstName?: string
  lastName?: string
}

type SortDirection = "asc" | "desc"
type SortKey = "name" | "sector" | "stage" | "status" | "date" | "agent" | "priority" | "revisit"
const ROWS_PER_PAGE = 50

interface PlaceListViewProps {
  places: PlaceRecord[]
  isLoading?: boolean
  onSelectPlace?: (place: PlaceRecord) => void
  stages?: SelectOption[]
  statuses?: SelectOption[]
  onChangeStage?: (place: PlaceRecord, stageId: string) => Promise<void> | void
  onChangeStatus?: (place: PlaceRecord, statusId: string) => Promise<void> | void
  onAssignAgent?: (place: PlaceRecord) => void
}

const PlaceListView: React.FC<PlaceListViewProps> = ({
  places,
  isLoading = false,
  onSelectPlace,
  stages = [],
  statuses = [],
  onChangeStage,
  onChangeStatus,
  onAssignAgent,
}) => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState("")
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({ key: "name", direction: "asc" })
  const [updating, setUpdating] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  const getImageUrl = (img?: string, img2?: string) => {
    const raw = img || img2
    if (!raw) return null
    return `${Settings.BasePathForAPI}file/getThumbnail?filename=${encodeURIComponent(raw)}`
  }

  const filteredPlaces = useMemo(() => places.filter(p => {
    if (!searchTerm.trim()) return true
    const term = searchTerm.toLowerCase()
    return (
      p.name.toLowerCase().includes(term) ||
      (p.address && p.address.toLowerCase().includes(term)) ||
      (p.sector && p.sector.toLowerCase().includes(term)) ||
      (p.city && p.city.toLowerCase().includes(term))
    )
  }), [places, searchTerm])

  const isPriority = (place: PlaceRecord) => typeof place.isInPriority === "boolean"
    ? place.isInPriority
    : Boolean(place.priorityAssignedTo || place.priorityTo || place.priorityAssignmentDate)

  const hasRevisit = (place: PlaceRecord) => Boolean(
    place.revisitPending || place.revisitAssignedTo || place.revisitTo || place.revisitAssignmentDate
  )

  const getAgentName = (place: PlaceRecord) =>
    `${place.assignedToObj?.firstName || ""} ${place.assignedToObj?.lastName || ""}`.trim()

  const getSortValue = (place: PlaceRecord, key: SortKey): string | number => {
    switch (key) {
      case "sector": return `${place.sector || ""} ${place.city || ""}`
      case "stage": return place.stage?.name || ""
      case "status": return place.placeStatus?.name || ""
      case "date": return new Date(place.updated || place.updatedDate || place.created || place.createdDate || 0).getTime()
      case "agent": return getAgentName(place)
      case "priority": return isPriority(place) ? 1 : 0
      case "revisit": return hasRevisit(place) ? 1 : 0
      default: return place.name || ""
    }
  }

  const sortedPlaces = useMemo(() => [...filteredPlaces].sort((a, b) => {
    const first = getSortValue(a, sort.key)
    const second = getSortValue(b, sort.key)
    const comparison = typeof first === "number" && typeof second === "number"
      ? first - second
      : String(first).localeCompare(String(second), "es", { numeric: true, sensitivity: "base" })
    return sort.direction === "asc" ? comparison : -comparison
  }), [filteredPlaces, sort])

  const totalPages = Math.max(1, Math.ceil(sortedPlaces.length / ROWS_PER_PAGE))
  const firstVisibleRecord = sortedPlaces.length === 0 ? 0 : page * ROWS_PER_PAGE + 1
  const lastVisibleRecord = Math.min((page + 1) * ROWS_PER_PAGE, sortedPlaces.length)
  const pagedPlaces = useMemo(
    () => sortedPlaces.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE),
    [page, sortedPlaces]
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm, places])

  useEffect(() => {
    if (page >= totalPages) setPage(totalPages - 1)
  }, [page, totalPages])

  const changeSort = (key: SortKey) => {
    setSort(current => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }))
  }

  const sortHeader = (label: string, key: SortKey, className?: string) => (
    <th className={className} aria-sort={sort.key === key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 border-0 text-decoration-none text-dark fw-bold text-nowrap"
        onClick={() => changeSort(key)}
      >
        {label}
        <i className={`fas ${sort.key === key ? (sort.direction === "asc" ? "fa-sort-up" : "fa-sort-down") : "fa-sort"} ms-1 text-muted`} />
      </button>
    </th>
  )

  const runUpdate = async (key: string, update: (() => Promise<void> | void) | undefined) => {
    if (!update) return
    try {
      setUpdating(key)
      await update()
    } finally {
      setUpdating(null)
    }
  }

  return (
    <Card className="border-0 shadow-sm rounded-3">
      <Card.Header className="bg-white py-3 border-bottom">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-list-ul text-primary me-1" />
            <span className="fw-bold text-dark fs-6">Lista de Lugares</span>
            <Badge bg="primary" pill className="ms-1">{sortedPlaces.length} registros</Badge>
            {sortedPlaces.length > 0 && (
              <small className="text-muted">Mostrando {firstVisibleRecord}–{lastVisibleRecord}</small>
            )}
          </div>
          <div className="flex-grow-1 flex-md-grow-0" style={{ minWidth: 220, maxWidth: 360 }}>
            <InputGroup size="sm" className="rounded-pill border shadow-2xs overflow-hidden">
              <InputGroup.Text className="bg-white border-0 pe-1 text-muted">
                <i className="fas fa-search" />
              </InputGroup.Text>
              <Form.Control
                className="border-0 ps-1 shadow-none"
                placeholder="Buscar por nombre, sector..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary spinner-border-sm me-2" role="status" />
            <span className="small text-muted">Cargando lista de lugares...</span>
          </div>
        ) : sortedPlaces.length === 0 ? (
          <div className="text-center py-5 text-muted small">
            <i className="fas fa-location-dot fa-2x mb-2 text-muted" />
            <div>No se encontraron lugares que coincidan con los criterios.</div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <Table hover responsive className="align-middle small mb-0 d-none d-md-table">
              <thead className="table-light">
                <tr>
                  <th className="ps-3 text-center" style={{ width: 60 }}>FOTO</th>
                  {sortHeader("LUGAR / UBICACIÓN", "name")}
                  {sortHeader("SECTOR / CIUDAD", "sector")}
                  {sortHeader("ETAPA", "stage")}
                  {sortHeader("ESTADO", "status")}
                  {sortHeader("FECHA Y ACTUALIZACIÓN", "date")}
                  {sortHeader("AGENTE ASIGNADO", "agent")}
                  {sortHeader("PRIORIDAD", "priority", "text-center")}
                  {sortHeader("RE-VISITA", "revisit", "text-center")}
                  <th className="text-end pe-3">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {pagedPlaces.map(place => {
                  const imgUrl = getImageUrl(place.image, place.image2)
                  const createdDate = place.created || place.createdDate
                  const updatedDate = place.updated || place.updatedDate
                  const hasActualUpdate = updatedDate && (!createdDate || new Date(updatedDate).getTime() !== new Date(createdDate).getTime())

                  return (
                    <tr key={place.id}>
                      {/* Foto */}
                      <td className="ps-3 text-center align-middle" style={{ width: 60 }}>
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            loading="lazy"
                            decoding="async"
                            alt={place.name}
                            className="rounded-3 border object-fit-cover shadow-2xs"
                            style={{ width: 44, height: 44, objectFit: "cover" }}
                            onError={e => {
                              (e.target as HTMLElement).style.display = "none"
                            }}
                          />
                        ) : (
                          <div
                            className="rounded-3 bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold border mx-auto"
                            style={{ width: 44, height: 44, fontSize: 16 }}
                          >
                            {place.name ? place.name.charAt(0).toUpperCase() : "L"}
                          </div>
                        )}
                      </td>

                      {/* Lugar / Ubicación */}
                      <td>
                        <div
                          className="fw-bold text-primary clickable"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            if (onSelectPlace) onSelectPlace(place)
                            else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                          }}
                        >
                          {place.name}
                        </div>
                        {place.address && <small className="text-muted d-block text-truncate" style={{ maxWidth: 220 }}>{place.address}</small>}
                      </td>

                      {/* Sector / Ciudad */}
                      <td>
                        <div className="fw-semibold text-dark">{place.sector || "—"}</div>
                        {place.city && <small className="text-muted d-block">{place.city}</small>}
                      </td>

                      {/* Etapa */}
                      <td>
                        <Form.Select
                          size="sm"
                          className="fw-semibold border-primary bg-primary bg-opacity-10"
                          style={{ minWidth: 155 }}
                          aria-label={`${t("Stage")} ${place.name}`}
                          value={place.stageId || ""}
                          disabled={!onChangeStage || updating === `stage-${place.id}`}
                          onChange={event => runUpdate(`stage-${place.id}`, () => onChangeStage?.(place, event.target.value))}
                        >
                          <option value="">{place.stage?.name || "Sin etapa"}</option>
                          {stages.map(stage => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
                        </Form.Select>
                      </td>

                      {/* Estado */}
                      <td>
                        <Form.Select
                          size="sm"
                          className="fw-semibold"
                          style={{
                            minWidth: 155,
                            borderColor: place.placeStatus?.color || "#6c757d",
                            color: place.placeStatus?.color || "#212529",
                            backgroundColor: "#fff",
                          }}
                          aria-label={`${t("Status")} ${place.name}`}
                          value={place.placeStatusId || ""}
                          disabled={!onChangeStatus || updating === `status-${place.id}`}
                          onChange={event => runUpdate(`status-${place.id}`, () => onChangeStatus?.(place, event.target.value))}
                        >
                          <option value="">{place.placeStatus?.name || "Sin estado"}</option>
                          {statuses.map(status => <option key={status.id} value={status.id}>{status.name}</option>)}
                        </Form.Select>
                      </td>

                      {/* Fecha y Actualización */}
                      <td>
                        <div>
                          {createdDate ? (
                            <span className="text-dark d-block fw-semibold" style={{ fontSize: "0.78rem" }}>
                              <i className="far fa-calendar-plus me-1 text-primary" />
                              {moment(createdDate).format("DD/MM/YYYY")}
                            </span>
                          ) : (
                            <span className="text-muted d-block" style={{ fontSize: "0.75rem" }}>Sin registro</span>
                          )}
                          {hasActualUpdate ? (
                            <small className="text-muted d-block" style={{ fontSize: "0.72rem" }} title={moment(updatedDate).format("DD/MM/YYYY HH:mm")}>
                              <i className="far fa-clock me-1 text-info" />
                              Act: {moment(updatedDate).fromNow()}
                            </small>
                          ) : (
                            <small className="text-muted d-block" style={{ fontSize: "0.72rem", opacity: 0.6 }}>
                              Sin cambios
                            </small>
                          )}
                        </div>
                      </td>

                      {/* Agente Asignado */}
                      <td>
                        <Button
                          variant="light"
                          size="sm"
                          className="w-100 border d-flex align-items-center justify-content-between gap-2 text-start"
                          title="Asignar o cambiar agente"
                          disabled={!onAssignAgent}
                          onClick={() => onAssignAgent?.(place)}
                        >
                          <span className={getAgentName(place) ? "text-dark text-truncate" : "text-muted"}>
                            <i className={`fas ${getAgentName(place) ? "fa-user-check text-success" : "fa-user-plus text-primary"} me-1`} />
                            {getAgentName(place) || "Asignar agente"}
                          </span>
                          <i className="fas fa-pen text-muted" aria-hidden="true" />
                        </Button>
                      </td>

                      {/* Prioridad */}
                      <td className="text-center">
                        <i
                          className={`fas fa-star fs-6 ${isPriority(place) ? "text-danger" : "text-muted opacity-25"}`}
                          title={isPriority(place) ? "Prioridad activa" : "Sin prioridad"}
                          aria-label={isPriority(place) ? "Prioridad activa" : "Sin prioridad"}
                        />
                      </td>

                      {/* Re-visita */}
                      <td className="text-center">
                        <i
                          className={`fas fa-rotate-left fs-6 ${hasRevisit(place) ? "text-warning" : "text-muted opacity-25"}`}
                          title={hasRevisit(place) ? "Re-visita pendiente" : "Sin re-visita pendiente"}
                          aria-label={hasRevisit(place) ? "Re-visita pendiente" : "Sin re-visita pendiente"}
                        />
                      </td>

                      {/* Acciones */}
                      <td className="text-end pe-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="py-1 px-2 rounded-3"
                          title="Ver detalles"
                          onClick={() => {
                            if (onSelectPlace) onSelectPlace(place)
                            else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                          }}
                        >
                          <i className="fas fa-eye" />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>

            {/* Mobile View: Responsive Card List */}
            <div className="d-md-none p-2 bg-light">
              {pagedPlaces.map(place => {
                const imgUrl = getImageUrl(place.image, place.image2)
                const createdDate = place.created || place.createdDate
                const updatedDate = place.updated || place.updatedDate
                const hasActualUpdate = updatedDate && (!createdDate || new Date(updatedDate).getTime() !== new Date(createdDate).getTime())

                return (
                  <Card key={`mob-${place.id}`} className="mb-2 border shadow-2xs rounded-3 overflow-hidden bg-white">
                    <Card.Body className="p-3">
                      <div className="d-flex gap-3 align-items-start">
                        {/* Foto */}
                        <div
                          className="flex-shrink-0 cursor-pointer"
                          onClick={() => {
                            if (onSelectPlace) onSelectPlace(place)
                            else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                          }}
                        >
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              loading="lazy"
                              alt={place.name}
                              className="rounded-3 border object-fit-cover shadow-2xs"
                              style={{ width: 56, height: 56, objectFit: "cover" }}
                              onError={e => {
                                (e.target as HTMLElement).style.display = "none"
                              }}
                            />
                          ) : (
                            <div
                              className="rounded-3 bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold border"
                              style={{ width: 56, height: 56, fontSize: 18 }}
                            >
                              {place.name ? place.name.charAt(0).toUpperCase() : "L"}
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex align-items-center justify-content-between gap-1">
                            <div
                              className="fw-bold text-primary text-truncate cursor-pointer"
                              style={{ fontSize: "0.95rem" }}
                              onClick={() => {
                                if (onSelectPlace) onSelectPlace(place)
                                else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                              }}
                            >
                              {place.name}
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                              {isPriority(place) && (
                                <i className="fas fa-star text-danger small" title="Prioridad activa" />
                              )}
                              {hasRevisit(place) && (
                                <i className="fas fa-rotate-left text-warning small" title="Re-visita pendiente" />
                              )}
                            </div>
                          </div>

                          {(place.sector || place.city) && (
                            <small className="text-muted d-block text-truncate mb-1">
                              <i className="fas fa-map-marker-alt me-1 text-danger opacity-75" />
                              {[place.sector, place.city].filter(Boolean).join(", ")}
                            </small>
                          )}

                          {/* Stage and Status selectors */}
                          <div className="d-flex flex-wrap gap-1 mt-2">
                            <Form.Select
                              size="sm"
                              className="fw-semibold border-primary bg-primary bg-opacity-10 flex-grow-1 py-1"
                              style={{ fontSize: "0.76rem" }}
                              aria-label={`${t("Stage")} ${place.name}`}
                              value={place.stageId || ""}
                              disabled={!onChangeStage || updating === `stage-${place.id}`}
                              onChange={event => runUpdate(`stage-${place.id}`, () => onChangeStage?.(place, event.target.value))}
                            >
                              <option value="">{place.stage?.name || "Sin etapa"}</option>
                              {stages.map(stage => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
                            </Form.Select>

                            <Form.Select
                              size="sm"
                              className="fw-semibold flex-grow-1 py-1"
                              style={{
                                fontSize: "0.76rem",
                                borderColor: place.placeStatus?.color || "#6c757d",
                                color: place.placeStatus?.color || "#212529",
                                backgroundColor: "#fff",
                              }}
                              aria-label={`${t("Status")} ${place.name}`}
                              value={place.placeStatusId || ""}
                              disabled={!onChangeStatus || updating === `status-${place.id}`}
                              onChange={event => runUpdate(`status-${place.id}`, () => onChangeStatus?.(place, event.target.value))}
                            >
                              <option value="">{place.placeStatus?.name || "Sin estado"}</option>
                              {statuses.map(status => <option key={status.id} value={status.id}>{status.name}</option>)}
                            </Form.Select>
                          </div>

                          {/* Dates & Action Row */}
                          <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                            <div style={{ fontSize: "0.74rem" }}>
                              <span className="text-dark fw-semibold me-2">
                                <i className="far fa-calendar-plus me-1 text-primary" />
                                {createdDate ? moment(createdDate).format("DD/MM/YYYY") : "—"}
                              </span>
                              {hasActualUpdate ? (
                                <small className="text-muted" title={moment(updatedDate).format("DD/MM/YYYY HH:mm")}>
                                  <i className="far fa-clock me-1 text-info" />
                                  {moment(updatedDate).fromNow()}
                                </small>
                              ) : null}
                            </div>

                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="py-0 px-2 rounded-pill fw-semibold"
                              style={{ fontSize: "0.75rem" }}
                              onClick={() => {
                                if (onSelectPlace) onSelectPlace(place)
                                else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                              }}
                            >
                              Ver Ficha
                              <i className="fas fa-chevron-right ms-1 small" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )
              })}
            </div>
          </>
        )}
      </Card.Body>
      {!isLoading && totalPages > 1 && (
        <Card.Footer className="bg-white border-top px-3 py-2">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalRecords={sortedPlaces.length}
            onPageChanged={setPage}
          />
        </Card.Footer>
      )}
    </Card>
  )
}

export default PlaceListView
